import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import parse from 'html-react-parser';
import { useQueryClient } from 'react-query';
import { enqueueSnackbar } from 'notistack';

import { translate as t } from 'hooks/useTranslations';
import useDraftManual from 'views/App/Portal/Policy/DraftManuals/_data_sources/useDraftManual';
import { ApprovedContent, ApprovedContentItem, getApprovedContentData } from './_data_sources/getApprovedContentData';
import useManageApprovedContent from './_data_sources/useManageApprovedContent';
import { Badge, Box, Button, Chip, CircularProgress, MenuItem, Select, Stack, Typography } from '@mui/material';
import { ArrowBackOutlined, Close as RemoveIcon } from '@mui/icons-material';
import { useInView } from 'react-intersection-observer';
import { QuickSearch } from 'components/_atoms';
import manualHelper from '../helpers/draftManualHelper';
import GridSection, { ColumnDef } from 'components/_layouts/GridSection';
import { GridCellParams, GridRowSelectionModel } from '@mui/x-data-grid-pro';
import _ from 'lodash';
import { neutral } from '_lex/_foundations/phxColors';
import TakeoverLayout from '_lex/_layouts/TakeoverLayout';
// import useIssuedManualGroups, { ManualGroup } from 'data_sources/issuedManuals/useIssuedManualGroups';

export type ChangeKey =
  | 'POLICY_ADDED'
  | 'PREFACE_ADDED'
  | 'POLICY_TITLE_EDIT'
  | 'PREFACE_CONTENT_EDIT'
  | 'POLICY_ENABLED'
  | 'POLICY_DISABLED'
  | 'SECTION_ENABLED'
  | 'SECTION_DISABLED'
  | 'SECTION_MOVED'
  | 'SECTION_ADDED'
  | 'SECTION_DELETED'
  | 'SECTION_TITLE_EDIT'
  | 'SECTION_CONTENT_EDIT'
  | 'SUBSECTION_ENABLED'
  | 'SUBSECTION_DISABLED'
  | 'SUBSECTION_MOVED'
  | 'SUBSECTION_ADDED'
  | 'SUBSECTION_DELETED'
  | 'SUBSECTION_TITLE_EDIT'
  | 'SUBSECTION_CONTENT_EDIT'
  | 'SECTION_EDIT_LEVEL_TAG_EDIT'
  | 'SUBSECTION_EDIT_LEVEL_TAG_EDIT';

const mapChangesToText = (changes: ChangeKey[]): string[] => {
  return changes.map((change) => t(`draftManuals.changeTypes.${change}`) || change);
};

const publishSteps = ['select_content', 'set_priority'];

const PublishApproved: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const manualQuery = useDraftManual(id);
  const [isSaving, setIsSaving] = useState(false);
  const [currentStep, setCurrentStep] = useState<string>(publishSteps[0]);
  const [approvedContent, setApprovededContent] = useState<ApprovedContent>([]);
  const [selectedContent, setSelectedContent] = useState<ApprovedContent>([]);
  const [contentSearch, setContentSearch] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [contentRef, contentInView] = useInView();
  const [showSelectedContent, setShowSelectedContent] = useState(false);
  const { publish: publishApprovedContent } = useManageApprovedContent();
  const changeSetSeverityOptions = ['MINOR', 'MAJOR', 'CRITICAL'].map((o) => ({
    value: o,
    label: t(`draftManuals.publishApproved.changeSetSeverity.${o}`),
  }));

  const [rowSelectionModel, setRowSelectionModel] = React.useState<GridRowSelectionModel>([]);
  const [selectedChangeFilter, setSelectedChangeFilter] = useState<string | null>(null);
  const [sortModel, setSortModel] = React.useState([{ field: 'title', sort: 'asc' }]);

  const fetchApprovedContent = async () => {
    setIsLoading(true);
    try {
      const data = await getApprovedContentData(id);
      setApprovededContent(data);
    } catch (error) {
      setApprovededContent([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchApprovedContent();
    }
  }, [id]);

  useEffect(() => {
    setSelectedContent(
      approvedContent.filter((x) => rowSelectionModel.includes(x.id)).map((x) => ({ ...x, changeSetSeverity: 'MINOR' }))
    );
  }, [approvedContent, rowSelectionModel]);

  const filteredApprovedContent = useMemo(() => {
    return approvedContent.filter((item) => {
      if (!selectedChangeFilter) return true;
      return item.changes.some((changeObj: any) => changeObj.change === selectedChangeFilter);
    });
  }, [approvedContent, selectedChangeFilter]);

  const filteredSelectedContent = useMemo(() => {
    return selectedContent.filter((item) => {
      if (!selectedChangeFilter) return true;
      return item.changes.some((changeObj: any) => changeObj.change === selectedChangeFilter);
    });
  }, [selectedContent, selectedChangeFilter]);

  const handleSaveClick = () => {
    setIsSaving(true);
    publishApprovedContent(
      id!,
      selectedContent,
      () => {
        setIsSaving(false);

        queryClient.invalidateQueries(['approved_content', id]);
        queryClient.invalidateQueries(['draft_manuals']);
        navigate('/admin/policy/draft-manuals');
      },
      (errors) => {
        setIsSaving(false);
        enqueueSnackbar(errors.message, {
          variant: 'error',
          autoHideDuration: 20000,
        });
      }
    );
  };

  const handleChangeSetSeverityChange = (id: string, value: string | null = 'MINOR') => {
    setSelectedContent((prev) => {
      const updatedContent = prev.map((x: ApprovedContentItem) => {
        if (x.id === id) {
          return { ...x, changeSetSeverity: value };
        }
        return x;
      });
      return updatedContent as ApprovedContent;
    });
  };

  const selectContentColumns: ColumnDef = useMemo(
    () => ({
      title: {
        header: t('draftManuals.publishApproved.selectContent.title'),
        flex: 2,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <span>{manualHelper.formatManualNodeTitle(row, manualQuery.data!)}</span>;
        },
        valueGetter: (value: any, row: any) => {
          return manualHelper.formatManualNodeTitle(row, manualQuery.data!);
        },
      },
      description: {
        header: 'Description',
        flex: 2,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          const changeTexts = mapChangesToText(row.changes.map((c: any) => c.change));
          return <span>{changeTexts.join(', ')}</span>;
        },
      },
    }),
    [t, manualQuery.data]
  );

  const setPriorityColumns: ColumnDef = useMemo(
    () => ({
      title: {
        header: t('draftManuals.publishApproved.setPriority.title'),
        flex: 2,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <span>{manualHelper.formatManualNodeTitle(row, manualQuery.data!)}</span>;
        },
        valueGetter: (value: any, row: any) => {
          return manualHelper.formatManualNodeTitle(row, manualQuery.data!);
        },
      },
      description: {
        header: 'Description',
        flex: 2,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          const changeTexts = mapChangesToText(row.changes.map((c: any) => c.change));
          return <span>{changeTexts.join(', ')}</span>;
        },
      },
      changeSetSeverity: {
        header: t('draftManuals.publishApproved.setPriority.changeSetSeverity'),
        flex: 1,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return (
            <Box>
              <Select
                label={false}
                value={row.changeSetSeverity || 'MINOR'}
                size='small'
                onChange={({ target }) => {
                  handleChangeSetSeverityChange(row.id, target.value);
                }}
                sx={{
                  minWidth: 100,
                }}
              >
                {changeSetSeverityOptions.map((opt) => (
                  <MenuItem
                    key={`opt_${opt.value}`}
                    value={opt.value}
                  >
                    {opt.label}
                  </MenuItem>
                ))}
              </Select>
            </Box>
          );
        },
      },
    }),
    [t, manualQuery.data]
  );

  const getNumericSortValue = (title: string) => {
    const match = title.match(/^(\d+)/);
    return match ? parseInt(match[1], 10) : Number.MAX_SAFE_INTEGER;
  };

  const getTitleSortValues = (aTitle: string, bTitle: string) => {
    const aNum = getNumericSortValue(aTitle);
    const bNum = getNumericSortValue(bTitle);
    const aHasNum = aNum !== Number.MAX_SAFE_INTEGER;
    const bHasNum = bNum !== Number.MAX_SAFE_INTEGER;

    return {
      aValue: aHasNum ? aNum : aTitle,
      bValue: bHasNum ? bNum : bTitle,
      aHasNum,
      bHasNum,
    };
  };

  const handleTitleSort = (aTitle: string, bTitle: string, sort: string) => {
    const titleValues = getTitleSortValues(aTitle, bTitle);

    if (titleValues.aHasNum !== titleValues.bHasNum) {
      return titleValues.aHasNum ? -1 : 1;
    }

    return { aValue: titleValues.aValue, bValue: titleValues.bValue, shouldContinue: true };
  };

  const applySorting = (data: ApprovedContent, sortModel: any[], manual: any) => {
    const newArrayData = data.map((item) => ({
      ...item,
      sortingTitle: manualHelper.formatManualNodeTitle(item, manual, true),
    }));

    if (sortModel && sortModel.length > 0) {
      const { field, sort } = sortModel[0];

      return newArrayData.sort((a, b) => {
        let aValue, bValue;

        switch (field) {
          case 'title':
          case 'sortingTitle':
            const aTitle = a.sortingTitle?.toString() || '';
            const bTitle = b.sortingTitle?.toString() || '';
            const titleResult = handleTitleSort(aTitle, bTitle, sort);

            // If it's a number, return immediately (mixed case handled)
            if (typeof titleResult === 'number') {
              return titleResult;
            }

            // Both are same type, continue with normal sorting
            aValue = titleResult.aValue;
            bValue = titleResult.bValue;
            break;
          case 'description':
            aValue = mapChangesToText(a.changes.map((c: any) => c.change)).join(', ');
            bValue = mapChangesToText(b.changes.map((c: any) => c.change)).join(', ');
            break;
          default:
            const defaultTitle_a = a.sortingTitle?.toString() || '';
            const defaultTitle_b = b.sortingTitle?.toString() || '';
            const defaultTitleResult = handleTitleSort(defaultTitle_a, defaultTitle_b, sort);

            // If it's a number, return immediately (mixed case handled)
            if (typeof defaultTitleResult === 'number') {
              return defaultTitleResult;
            }

            // Both are same type, continue with normal sorting
            aValue = defaultTitleResult.aValue;
            bValue = defaultTitleResult.bValue;
        }

        if (sort === 'asc') {
          if (typeof aValue === 'number' && typeof bValue === 'number') {
            return aValue - bValue;
          }
          return aValue.toString().localeCompare(bValue.toString());
        } else {
          if (typeof aValue === 'number' && typeof bValue === 'number') {
            return bValue - aValue;
          }
          return bValue.toString().localeCompare(aValue.toString());
        }
      });
    }

    return newArrayData;
  };

  const sortGridData = useMemo(() => {
    const sortedData = applySorting(filteredApprovedContent, sortModel, manualQuery.data);
    // Add searchable title for filtering
    const dataWithSearchableTitle = sortedData.map((item) => ({
      ...item,
      searchableTitle: manualHelper.formatManualNodeTitle(item, manualQuery.data!, true) as string,
    }));

    // Filter by contentSearch
    if (contentSearch && contentSearch.trim() !== '') {
      const searchLower = contentSearch.toLowerCase();
      return dataWithSearchableTitle.filter((item) => item.searchableTitle?.toLowerCase().includes(searchLower));
    }

    return dataWithSearchableTitle;
  }, [filteredApprovedContent, sortModel, manualQuery.data, contentSearch]);

  const handleSortModelChange = (model: any) => {
    setSortModel(model);
  };

  const sortSelectedData = useMemo(() => {
    const sortedData = applySorting(filteredSelectedContent, sortModel, manualQuery.data);
    // Add searchable title for filtering
    const dataWithSearchableTitle = sortedData.map((item) => ({
      ...item,
      searchableTitle: manualHelper.formatManualNodeTitle(item, manualQuery.data!, true) as string,
    }));

    // Filter by contentSearch
    if (contentSearch && contentSearch.trim() !== '') {
      const searchLower = contentSearch.toLowerCase();
      return dataWithSearchableTitle.filter((item) => item.searchableTitle?.toLowerCase().includes(searchLower));
    }

    return dataWithSearchableTitle;
  }, [filteredSelectedContent, sortModel, manualQuery.data, contentSearch]);

  return (
    <TakeoverLayout
      backUrl='/admin/policy/draft-manuals'
      pageTitle={manualQuery.data ? parse(manualQuery.data?.name ?? '') : ''}
      pageToolbarLeft={
        <Stack
          direction='row'
          spacing={3}
          alignItems='center'
        >
          <QuickSearch
            id='contents_search'
            initialValue={contentSearch}
            onChange={(val) => setContentSearch(val)}
            placeholder={t('draftManuals.publishApproved.search')}
          />
          <Box sx={{ minWidth: 300 }}>
            <Select
              value={selectedChangeFilter || ''}
              onChange={(event) => setSelectedChangeFilter(event.target.value || null)}
              displayEmpty
              size='small'
              variant='outlined'
              fullWidth
              sx={{
                minWidth: 300,
                height: 36,
                '& .MuiSelect-select': {
                  padding: '8px 14px',
                },
              }}
            >
              <MenuItem value=''>
                <em>All Changes</em>
              </MenuItem>
              {(
                [
                  'POLICY_ADDED',
                  'PREFACE_ADDED',
                  'POLICY_TITLE_EDIT',
                  'PREFACE_CONTENT_EDIT',
                  'POLICY_ENABLED',
                  'POLICY_DISABLED',
                  'SECTION_ENABLED',
                  'SECTION_DISABLED',
                  'SECTION_MOVED',
                  'SECTION_ADDED',
                  'SECTION_DELETED',
                  'SECTION_TITLE_EDIT',
                  'SECTION_CONTENT_EDIT',
                  'SUBSECTION_ENABLED',
                  'SUBSECTION_DISABLED',
                  'SUBSECTION_MOVED',
                  'SUBSECTION_ADDED',
                  'SUBSECTION_DELETED',
                  'SUBSECTION_TITLE_EDIT',
                  'SUBSECTION_CONTENT_EDIT',
                  'SECTION_EDIT_LEVEL_TAG_EDIT',
                  'SUBSECTION_EDIT_LEVEL_TAG_EDIT',
                ] as ChangeKey[]
              ).map((key) => (
                <MenuItem
                  key={key}
                  value={key}
                >
                  {t(`draftManuals.changeTypes.${key}`)}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Stack>
      }
      footerContent={
        currentStep === 'select_content' ? (
          <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', pb: 2, width: '100%' }}>
            <Button
              variant='outlined'
              color='neutral'
              onClick={() => navigate('/admin/policy/draft-manuals')}
            >
              {t('common.cancel')}
            </Button>
            <Button
              onClick={() => setCurrentStep('set_priority')}
              disabled={isSaving || selectedContent.length === 0}
              component='button'
              type='submit'
              variant='contained'
            >
              {t('common.continue')}
            </Button>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', pb: 2, width: '100%' }}>
            <Button
              variant='outlined'
              color='neutral'
              onClick={() => navigate('/admin/policy/draft-manuals')}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant='outlined'
              color='neutral'
              onClick={() => setCurrentStep('select_content')}
              startIcon={<ArrowBackOutlined />}
            >
              {t('common.back')}
            </Button>
            <Button
              onClick={handleSaveClick}
              disabled={isSaving}
              component='button'
              type='submit'
              variant='contained'
              endIcon={isSaving ? <CircularProgress size={16} /> : null}
            >
              {t('draftManuals.publishApproved.save')}
            </Button>
          </Box>
        )
      }
    >
      {currentStep === 'select_content' && (
        <Stack spacing={2}>
          <Box sx={{ px: 4 }}>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                justifyContent: 'start',
                gap: 3,
              }}
            >
              {selectedContent.length > 3 && (
                <Button
                  className='xs'
                  variant='outlined'
                  size='small'
                  onClick={() => setShowSelectedContent(!showSelectedContent)}
                  endIcon={
                    showSelectedContent ? null : (
                      <Badge
                        variant='inline'
                        color='primary'
                        badgeContent={selectedContent.length.toString()}
                      />
                    )
                  }
                >
                  {showSelectedContent
                    ? t('draftManuals.publishApproved.selectContent.showLess')
                    : t('draftManuals.publishApproved.selectContent.showAll')}
                </Button>
              )}
              <Button
                className='xs'
                variant='text'
                size='small'
                onClick={() => setRowSelectionModel([])}
              >
                {t('draftManuals.publishApproved.selectContent.clearAll')}
              </Button>
              <Box
                sx={{
                  height: 32,
                  border: `solid 1px ${neutral[300]}`,
                }}
              ></Box>
              {_.take(selectedContent, showSelectedContent ? selectedContent.length : 3).map((content) => (
                <Chip
                  key={`selected_content_${content.id}`}
                  label={manualHelper.formatManualNodeTitle(content, manualQuery.data!)}
                  onDelete={() => setRowSelectionModel((prev) => prev.filter((x) => x !== content.id))}
                  deleteIcon={<RemoveIcon />}
                  size='small'
                  sx={{
                    justifyContent: 'space-between',
                    gap: 2,
                    height: 32,
                    minHeight: 32,
                    backgroundColor: neutral[50],
                    '& .MuiChip-label': {
                      lineHeight: 32,
                      padding: 2,
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    },
                  }}
                />
              ))}
            </Box>
          </Box>
          <GridSection
            columns={selectContentColumns}
            data={sortGridData}
            totalCount={sortGridData.length}
            gridProps={{
              checkboxSelection: true,
              onRowSelectionModelChange: (newRowSelectionModel: GridRowSelectionModel) => {
                setRowSelectionModel(newRowSelectionModel);
              },
              rowSelectionModel,
            }}
            isLoading={isLoading}
            sorting={sortModel}
            onSortModelChange={handleSortModelChange}
          />
        </Stack>
      )}
      {currentStep === 'set_priority' && (
        <Stack spacing={2}>
          <GridSection
            columns={setPriorityColumns}
            data={sortSelectedData}
            totalCount={sortSelectedData.length}
            gridProps={{}}
            isLoading={isLoading}
            sorting={sortModel}
            onSortModelChange={handleSortModelChange}
          />
        </Stack>
      )}
    </TakeoverLayout>
  );
};

export default PublishApproved;
